import React, { useState, useMemo, useEffect } from 'react';
import {
  Search, Filter, Download, Mail, Phone, Calendar,
  TrendingUp, Star, Clock, MessageSquare,
  SortAsc, SortDesc, ChevronDown, Trash2, RefreshCw, Users, Video
} from 'lucide-react';
import { getAllLeads, getLeadStats, deleteLead, exportLeadsCSV } from '../../services/leadServices';
import { getAllWebinarRegistrations } from '../../services/roomServices';
import { toast } from 'react-toastify';

export default function LeadsTab() {
  const [activeTab, setActiveTab] = useState('leads');
  // Leads state
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);

  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [regStats, setRegStats] = useState(null);
  const [isLoadingRegs, setIsLoadingRegs] = useState(false);
  const [regSearchQuery, setRegSearchQuery] = useState('');
  const [regFilterType, setRegFilterType] = useState('all');
  const [regSortOrder, setRegSortOrder] = useState('desc');
  const [selectedReg, setSelectedReg] = useState(null);
  const [regWebinarFilter, setRegWebinarFilter] = useState('all');

  // Fetch leads and stats
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [leadsResponse, statsResponse] = await Promise.all([
        getAllLeads({ limit: 500 }),
        getLeadStats()
      ]);

      if (leadsResponse.success) {
        setLeads(leadsResponse.data || []);
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch registrations
  const fetchRegistrations = async () => {
    setIsLoadingRegs(true);
    try {
      const response = await getAllWebinarRegistrations();
      if (response.success) {
        setRegistrations(response.registrations || []);
        setRegStats(response.stats || null);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
    } finally {
      setIsLoadingRegs(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'registrations' && registrations.length === 0 && !isLoadingRegs) {
      fetchRegistrations();
    }
  }, [activeTab]);

  // Get unique lead capture types
  const allTypes = useMemo(() => {
    const types = new Set();
    leads.forEach(lead => {
      if (lead.leadCaptureType) types.add(lead.leadCaptureType);
    });
    return Array.from(types);
  }, [leads]);

  // Get unique webinar names for filter
  const webinarNames = useMemo(() => {
    const names = new Set();
    registrations.forEach(r => {
      if (r.webinarName) names.add(r.webinarName);
    });
    return Array.from(names);
  }, [registrations]);

  // Filter and sort leads
  const filteredLeads = useMemo(() => {
    let filtered = leads.filter(lead => {
      const searchLower = searchQuery.toLowerCase();
      const formData = lead.formData || {};
      const matchesSearch = !searchQuery ||
        formData.email?.toLowerCase().includes(searchLower) ||
        formData.name?.toLowerCase().includes(searchLower) ||
        formData.company?.toLowerCase().includes(searchLower) ||
        lead.participantName?.toLowerCase().includes(searchLower) ||
        lead.leadCaptureName?.toLowerCase().includes(searchLower);

      const matchesType = filterType === 'all' || lead.leadCaptureType === filterType;

      return matchesSearch && matchesType;
    });

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name':
          aVal = (a.formData?.name || a.participantName || '').toLowerCase();
          bVal = (b.formData?.name || b.participantName || '').toLowerCase();
          return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        case 'created_date':
        default:
          aVal = new Date(a.submittedAt || a.createdAt || 0).getTime();
          bVal = new Date(b.submittedAt || b.createdAt || 0).getTime();
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    return filtered;
  }, [leads, searchQuery, sortBy, sortOrder, filterType]);

  // Filter and sort registrations
  const filteredRegistrations = useMemo(() => {
    let filtered = registrations.filter(reg => {
      const searchLower = regSearchQuery.toLowerCase();
      const matchesSearch = !regSearchQuery ||
        reg.firstName?.toLowerCase().includes(searchLower) ||
        reg.lastName?.toLowerCase().includes(searchLower) ||
        reg.email?.toLowerCase().includes(searchLower) ||
        reg.phone?.toLowerCase().includes(searchLower) ||
        reg.webinarName?.toLowerCase().includes(searchLower);

      const matchesType = regFilterType === 'all' || reg.type === regFilterType;
      const matchesWebinar = regWebinarFilter === 'all' || reg.webinarName === regWebinarFilter;

      return matchesSearch && matchesType && matchesWebinar;
    });

    filtered.sort((a, b) => {
      const aVal = new Date(a.registeredAt || 0).getTime();
      const bVal = new Date(b.registeredAt || 0).getTime();
      return regSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    return filtered;
  }, [registrations, regSearchQuery, regFilterType, regSortOrder, regWebinarFilter]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
    });
  };

  // Get type badge color
  const getTypeBadge = (type) => {
    const colors = {
      'email_capture': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'lead_capture': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'sms_optin': 'bg-green-500/20 text-green-400 border-green-500/30',
      'lead_magnet': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'replay_access': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      'calendar_booking': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'quiz': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
    return colors[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  // Format type label
  const formatTypeLabel = (type) => {
    if (!type) return 'Lead';
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle export leads
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await exportLeadsCSV();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Leads exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export leads');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle export registrations
  const handleExportRegistrations = () => {
    if (filteredRegistrations.length === 0) return;
    const headers = ['#', 'First Name', 'Last Name', 'Email', 'Phone', 'Webinar', 'Registered Date', 'Type'];
    const rows = filteredRegistrations.map((r, i) => [
      i + 1,
      r.firstName || '',
      r.lastName || '',
      r.email || '',
      r.phone || '',
      r.webinarName || '',
      r.registeredAt ? new Date(r.registeredAt).toLocaleString() : '',
      r.type === 'post' ? 'Replay' : 'Pre-event',
    ]);
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `registrations-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Registrations exported successfully');
  };

  // Handle delete lead
  const handleDelete = async (leadId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    setIsDeleting(leadId);
    try {
      const response = await deleteLead(leadId);
      if (response.success) {
        setLeads(prev => prev.filter(l => l._id !== leadId));
        if (selectedLead?._id === leadId) setSelectedLead(null);
        toast.success('Lead deleted');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete lead');
    } finally {
      setIsDeleting(null);
    }
  };

  if (isLoading && activeTab === 'leads') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads & Registrations</h1>
          <p className="text-gray-400 text-sm mt-1">
            {activeTab === 'leads'
              ? `${filteredLeads.length} ${filteredLeads.length === 1 ? 'lead' : 'leads'}${searchQuery ? ' matching your filters' : ''}`
              : `${filteredRegistrations.length} ${filteredRegistrations.length === 1 ? 'registration' : 'registrations'}${regSearchQuery ? ' matching your filters' : ''}`
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={activeTab === 'leads' ? fetchData : fetchRegistrations}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg text-white transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${(activeTab === 'leads' ? isLoading : isLoadingRegs) ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={activeTab === 'leads' ? handleExport : handleExportRegistrations}
            disabled={activeTab === 'leads' ? (isExporting || leads.length === 0) : filteredRegistrations.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className={`w-4 h-4 ${isExporting ? 'animate-bounce' : ''}`} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => setActiveTab('leads')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'leads'
              ? 'text-purple-400 border-purple-500'
              : 'text-gray-400 border-transparent hover:text-white hover:border-white/20'
          }`}
        >
          <Star className="w-4 h-4" />
          Leads
          {stats && <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-white/10">{stats.totalLeads}</span>}
        </button>
        <button
          onClick={() => setActiveTab('registrations')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'registrations'
              ? 'text-purple-400 border-purple-500'
              : 'text-gray-400 border-transparent hover:text-white hover:border-white/20'
          }`}
        >
          <Users className="w-4 h-4" />
          Registrations
          {regStats && <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-white/10">{regStats.total}</span>}
        </button>
      </div>

      {/* ─── LEADS TAB ─── */}
      {activeTab === 'leads' && (
        <>
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-purple-400 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">Total Leads</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.totalLeads}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-green-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs">Today</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.leadsToday}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-blue-400 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs">This Week</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.leadsThisWeek}</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center gap-2 text-orange-400 mb-1">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-xs">This Month</span>
                </div>
                <p className="text-2xl font-bold text-white">{stats.leadsThisMonth}</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white/5 rounded-xl p-4 space-y-4 border border-white/10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, email, or company..."
                    className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                  />
                </div>
              </div>
              <div className="relative">
                <Filter className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500/50"
                >
                  <option value="all" className="bg-[#1a1a2e] text-white">All Types</option>
                  {allTypes.map(type => (
                    <option key={type} value={type} className="bg-[#1a1a2e] text-white">{formatTypeLabel(type)}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Sort by:</span>
              <button
                onClick={() => setSortBy('created_date')}
                className={`text-xs px-2 py-1 rounded ${sortBy === 'created_date' ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400 hover:text-white'}`}
              >
                Date
              </button>
              <button
                onClick={() => setSortBy('name')}
                className={`text-xs px-2 py-1 rounded ${sortBy === 'name' ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400 hover:text-white'}`}
              >
                Name
              </button>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="ml-2 p-1 hover:bg-white/10 rounded"
              >
                {sortOrder === 'asc' ? <SortAsc className="w-4 h-4 text-gray-400" /> : <SortDesc className="w-4 h-4 text-gray-400" />}
              </button>
            </div>
          </div>

          {/* Leads List */}
          {filteredLeads.length === 0 ? (
            <div className="bg-white/5 rounded-xl p-12 text-center border border-white/10">
              <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No leads found</p>
              <p className="text-gray-500 text-sm mt-2">
                {searchQuery ? 'Try adjusting your filters' : 'Leads will appear here when forms are submitted during your meetings'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLeads.map(lead => {
                const data = lead.formData || {};
                const displayName = data.name || data.firstName || lead.participantName || 'Anonymous';
                const displayEmail = data.email || '';

                return (
                  <div
                    key={lead._id}
                    onClick={() => setSelectedLead(selectedLead?._id === lead._id ? null : lead)}
                    className="bg-white/5 hover:bg-white/10 rounded-xl p-4 cursor-pointer transition-all border border-transparent hover:border-white/10"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="text-white font-medium">{displayName}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full border ${getTypeBadge(lead.leadCaptureType)}`}>
                            {formatTypeLabel(lead.leadCaptureType)}
                          </span>
                          {lead.leadCaptureName && (
                            <span className="text-xs text-gray-500">
                              via {lead.leadCaptureName}
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {displayEmail && (
                            <div className="flex items-center gap-2 text-gray-400">
                              <Mail className="w-3.5 h-3.5" />
                              <span className="truncate">{displayEmail}</span>
                            </div>
                          )}
                          {data.phone && (
                            <div className="flex items-center gap-2 text-gray-400">
                              <Phone className="w-3.5 h-3.5" />
                              <span>{data.phone}</span>
                            </div>
                          )}
                          {data.company && (
                            <div className="flex items-center gap-2 text-gray-400">
                              <Star className="w-3.5 h-3.5" />
                              <span>{data.company}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-gray-400">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{formatDate(lead.submittedAt || lead.createdAt)}</span>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {selectedLead?._id === lead._id && (
                          <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                            <div className="bg-white/5 rounded-lg p-3">
                              <h4 className="text-sm font-medium text-white mb-2">Form Data</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                {Object.entries(data).map(([key, value]) => (
                                  <div key={key} className="flex gap-2">
                                    <span className="text-gray-500 capitalize min-w-[100px]">{key.replace(/_/g, ' ')}:</span>
                                    <span className="text-gray-300">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400">
                              {lead.meetingId && (
                                <div className="flex items-center gap-2">
                                  <span>Meeting:</span>
                                  <span className="text-gray-300">{lead.meetingId}</span>
                                </div>
                              )}
                              {lead.sessionId && (
                                <div className="flex items-center gap-2">
                                  <span>Session:</span>
                                  <span className="text-gray-300">{lead.sessionId}</span>
                                </div>
                              )}
                              {lead.consentGiven && (
                                <span className="px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full">
                                  Consent Given
                                </span>
                              )}
                              {lead.webhookSent && (
                                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full">
                                  Webhook Sent
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3 ml-4">
                        <button
                          onClick={(e) => handleDelete(lead._id, e)}
                          disabled={isDeleting === lead._id}
                          className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete lead"
                        >
                          {isDeleting === lead._id ? (
                            <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${selectedLead?._id === lead._id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ─── REGISTRATIONS TAB ─── */}
      {activeTab === 'registrations' && (
        <>
          {isLoadingRegs ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading registrations...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              {regStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center gap-2 text-purple-400 mb-1">
                      <Users className="w-4 h-4" />
                      <span className="text-xs">Total Registrations</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{regStats.total}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center gap-2 text-green-400 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs">Today</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{regStats.today}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center gap-2 text-blue-400 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-xs">This Week</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{regStats.thisWeek}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex items-center gap-2 text-orange-400 mb-1">
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-xs">This Month</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{regStats.thisMonth}</p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="bg-white/5 rounded-xl p-4 space-y-4 border border-white/10">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="relative">
                      <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={regSearchQuery}
                        onChange={(e) => setRegSearchQuery(e.target.value)}
                        placeholder="Search by name, email, phone..."
                        className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                      />
                    </div>
                  </div>

                  {/* Webinar Filter */}
                  <div className="relative">
                    <Video className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <select
                      value={regWebinarFilter}
                      onChange={(e) => setRegWebinarFilter(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500/50"
                    >
                      <option value="all" className="bg-[#1a1a2e] text-white">All Webinars</option>
                      {webinarNames.map(name => (
                        <option key={name} value={name} className="bg-[#1a1a2e] text-white">{name}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>

                  {/* Type Filter */}
                  <div className="relative">
                    <Filter className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <select
                      value={regFilterType}
                      onChange={(e) => setRegFilterType(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-[#1a1a2e] border border-white/10 rounded-lg text-white appearance-none cursor-pointer focus:outline-none focus:border-purple-500/50"
                    >
                      <option value="all" className="bg-[#1a1a2e] text-white">All Types</option>
                      <option value="pre" className="bg-[#1a1a2e] text-white">Pre-event</option>
                      <option value="post" className="bg-[#1a1a2e] text-white">Replay</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">Sort by date:</span>
                  <button
                    onClick={() => setRegSortOrder(regSortOrder === 'asc' ? 'desc' : 'asc')}
                    className="ml-2 p-1 hover:bg-white/10 rounded flex items-center gap-1 text-xs text-gray-400"
                  >
                    {regSortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
                    {regSortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Registrations List */}
              {filteredRegistrations.length === 0 ? (
                <div className="bg-white/5 rounded-xl p-12 text-center border border-white/10">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 text-lg">No registrations found</p>
                  <p className="text-gray-500 text-sm mt-2">
                    {regSearchQuery ? 'Try adjusting your filters' : 'Registrations will appear here when people register for your webinars'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredRegistrations.map((reg, idx) => {
                    const displayName = [reg.firstName, reg.lastName].filter(Boolean).join(' ') || 'Anonymous';
                    const regKey = `${reg.email}-${reg.webinarId}-${idx}`;

                    return (
                      <div
                        key={regKey}
                        onClick={() => setSelectedReg(selectedReg === regKey ? null : regKey)}
                        className="bg-white/5 hover:bg-white/10 rounded-xl p-4 cursor-pointer transition-all border border-transparent hover:border-white/10"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <h3 className="text-white font-medium">{displayName}</h3>
                              <span className={`px-2 py-0.5 text-xs rounded-full border ${
                                reg.type === 'post'
                                  ? 'bg-pink-500/20 text-pink-400 border-pink-500/30'
                                  : 'bg-green-500/20 text-green-400 border-green-500/30'
                              }`}>
                                {reg.type === 'post' ? 'Replay' : 'Pre-event'}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              {reg.email && (
                                <div className="flex items-center gap-2 text-gray-400">
                                  <Mail className="w-3.5 h-3.5" />
                                  <span className="truncate">{reg.email}</span>
                                </div>
                              )}
                              {reg.phone && (
                                <div className="flex items-center gap-2 text-gray-400">
                                  <Phone className="w-3.5 h-3.5" />
                                  <span>{reg.phone}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-gray-400">
                                <Video className="w-3.5 h-3.5" />
                                <span className="truncate">{reg.webinarName}</span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-400">
                                <Clock className="w-3.5 h-3.5" />
                                <span>{formatDate(reg.registeredAt)}</span>
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {selectedReg === regKey && (
                              <div className="mt-4 pt-4 border-t border-white/10">
                                <div className="bg-white/5 rounded-lg p-3">
                                  <h4 className="text-sm font-medium text-white mb-2">Registration Details</h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">First Name:</span>
                                      <span className="text-gray-300">{reg.firstName || '-'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Last Name:</span>
                                      <span className="text-gray-300">{reg.lastName || '-'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Email:</span>
                                      <span className="text-gray-300">{reg.email || '-'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Phone:</span>
                                      <span className="text-gray-300">{reg.phone || '-'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Webinar:</span>
                                      <span className="text-gray-300">{reg.webinarName}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Webinar Date:</span>
                                      <span className="text-gray-300">{reg.webinarDate ? formatFullDate(reg.webinarDate) : '-'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Registered At:</span>
                                      <span className="text-gray-300">{reg.registeredAt ? formatFullDate(reg.registeredAt) : '-'}</span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-gray-500 min-w-[100px]">Type:</span>
                                      <span className="text-gray-300">{reg.type === 'post' ? 'Replay Access' : 'Pre-event Registration'}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-3 ml-4">
                            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${selectedReg === regKey ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
